import { ArrowLeft, EditPencil, Link as LinkIcon } from 'iconoir-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { ViewOnlyBadge } from 'components/blocks/view-only-badge';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import type { IToolDetails } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useIsOwner } from 'hooks/use-is-owner';
import { getToolVisibility } from './tool-detail-utils';
type ToolDetailsTab = 'authentication' | 'api' | 'tutorial-guide' | 'dashboard' | 'connections';

type ToolsListTab = 'tool' | 'mcp-server' | 'blocks-tools';

type ToolDetailsTabItem = {
  value: ToolDetailsTab;
  label: string;
  labelKey?: string;
};

const DEFAULT_TABS: ToolDetailsTabItem[] = [
  { value: 'authentication', labelKey: 'AUTHENTICATION', label: 'Authentication' },
  { value: 'api', labelKey: 'API', label: 'API' },
  { value: 'tutorial-guide', labelKey: 'TUTORIAL_GUIDE', label: 'Tutorial Guide' },
  { value: 'dashboard', labelKey: 'DASHBOARD', label: 'Dashboard' },
];

export function ToolDetailsLayout({
  tool,
  tab,
  onTabChange,
  children,
  associatedAgentsCount,
  tabs,
  onEdit,
  isLoading = false,
}: {
  tool: IToolDetails;
  tab: ToolDetailsTab;
  onTabChange: (value: ToolDetailsTab) => void;
  children: React.ReactNode;
  associatedAgentsCount: number;
  tabs?: ToolDetailsTabItem[];
  onEdit?: () => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const canEdit = useIsOwner(tool);
  const fromTab = (location.state as { fromTab?: ToolsListTab } | null)?.fromTab;

  const backTo = (() => {
    if (fromTab) {
      return `/tools?tab=${fromTab}`;
    }

    if (tool.is_open_tool) {
      return '/tools?tab=blocks-tools';
    }

    if (tool.type === 'mcp_server') {
      return '/tools?tab=mcp-server';
    }

    if (tool.type === 'composio') {
      return '/tools?tab=composio';
    }

    return '/tools?tab=tool';
  })();

  const getTabLabel = (item: ToolDetailsTabItem) =>
    item.labelKey ? t(item.labelKey) || item.label : item.label;

  const visibleTabs = (tabs || DEFAULT_TABS).filter(
    (item) => item.value !== 'dashboard' || !tool.is_open_tool
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 sm:gap-6">
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        <Link to={backTo} className="shrink-0">
          <Button variant="ghost" size="icon" className="-ml-2 h-9 w-9 hover:bg-transparent sm:-ml-3 sm:h-10 sm:w-10">
            <ArrowLeft width={20} height={20} strokeWidth={1.75} className="dark:text-[#FFFFFF]" />
          </Button>
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
          <h1 className="truncate text-lg font-bold sm:text-2xl">{tool.name}</h1>
          {!isLoading && canEdit && onEdit ? (
            <Button variant="ghost" size="icon" className="text-primary" onClick={onEdit}>
              <EditPencil width={16} height={16} strokeWidth={1.75} className="dark:text-[#FFFFFF]" />
            </Button>
          ) : null}
          <VisibilityBadge value={getToolVisibility(tool)} className="shrink-0" />
        </div>
        {isLoading ? (
          <div
            className="flex w-full shrink-0 flex-col gap-1.5 rounded-lg border border-border p-3 sm:ml-auto sm:max-w-[240px]"
            aria-hidden
          >
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-full" />
          </div>
        ) : !canEdit ? (
          <ViewOnlyBadge
            className="max-w-[240px] sm:ml-auto"
            description={
              t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
              'You are not the owner of this tool.'
            }
          />
        ) : null}
      </div>

      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          value={tab}
          onValueChange={(value) => onTabChange(value as ToolDetailsTab)}
          className="min-w-0 w-full lg:w-auto lg:shrink-0"
        >
          <div className="w-full overflow-x-auto overscroll-x-contain lg:overflow-visible">
            <TabsList className="inline-flex h-10 w-max max-w-none flex-nowrap justify-start overflow-visible rounded-md border border-border bg-muted p-1 shadow-none">
              {visibleTabs.map((item) => (
                <TabsTrigger
                  key={item.value}
                  value={item.value}
                  className="h-8 shrink-0 rounded-md bg-transparent px-4 py-1 text-sm font-medium text-muted-foreground hover:bg-background/80 hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  {getTabLabel(item)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        {!tool.is_open_tool ? (
          <div className="flex w-fit shrink-0 flex-row items-center gap-2 self-start rounded-sm bg-muted px-2 py-1.5 lg:ml-auto h-10">
            <LinkIcon width={14} height={14} strokeWidth={1.75} className="text-primary dark:text-[#FFFFFF]" />
            <span className="text-sm text-muted-foreground">
              {t('ASSOCIATED_AGENTS') || 'Associated Agents'}:
            </span>
            <span className="text-sm font-medium text-foreground">{associatedAgentsCount}</span>
          </div>
        ) : null}

      </div>


      <div key={location.key} className="min-w-0 w-full">
        {children}
      </div>

    </div>
  );
}
